import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

import {
  WorkRestrictionRule,
  WorkRestrictionRulesResponse,
  WorkRestrictionRulesParams,
  CreateWorkRestrictionRulePayload,
  SectorDistrictsResponse,
  WorkTypesResponse,
} from './work-restrictions-rules.interfaces';

@Injectable({
  providedIn: 'root',
})
export class WorkRestrictionRulesService {
  private readonly baseUrl = '/schedules/rules';
  private readonly sectorDistrictsUrl = '/sectors/sector';
  private readonly workTypesUrl = '/schedules/work_types';
  private readonly workTypesPageLimit = 30;

  constructor(private http: HttpClient) {}

  /**
   * Получить типы работ (с автоматической догрузкой всех страниц)
   */
  getWorkTypes(): Observable<WorkTypesResponse> {
    const limit = this.workTypesPageLimit;
    let offset = 0;

    return this.fetchWorkTypesPage(limit, offset).pipe(
      expand((response) => {
        const received = response.data.work_types.length;
        if (received < limit) {
          return EMPTY;
        }
        offset += received;
        return this.fetchWorkTypesPage(limit, offset);
      }),
      reduce<WorkTypesResponse, WorkTypesResponse | null>((acc, response) => {
        if (!acc) {
          return response;
        }
        return Object.assign({}, response, {
          data: Object.assign({}, response.data, {
            work_types: acc.data.work_types.concat(response.data.work_types),
          }),
        });
      }, null),
      map((result) => result as WorkTypesResponse),
    );
  }

  private fetchWorkTypesPage(limit: number, offset: number): Observable<WorkTypesResponse> {
    const params = new HttpParams().set('limit', limit.toString()).set('offset', offset.toString());
    return this.http.get<WorkTypesResponse>(this.workTypesUrl + '/', { params });
  }

  /**
   * Получить список правил ограничений работ
   */
  getRules(params?: WorkRestrictionRulesParams): Observable<WorkRestrictionRulesResponse> {
    let httpParams = new HttpParams();

    if (params) {
      if (params.district_id !== undefined) {
        // Поддержка массива ID через запятую
        const districtIds = Array.isArray(params.district_id) ? params.district_id.join(',') : params.district_id.toString();
        httpParams = httpParams.set('district_id', districtIds);
      }
      if (params.work_type_id !== undefined) {
        // Поддержка массива ID через запятую
        const workTypeIds = Array.isArray(params.work_type_id) ? params.work_type_id.join(',') : params.work_type_id;
        httpParams = httpParams.set('work_type_id', workTypeIds);
      }
      if (params.rule_distribution_id !== undefined) {
        httpParams = httpParams.set('rule_distribution_id', params.rule_distribution_id.toString());
      }
      if (params.limit !== undefined) {
        httpParams = httpParams.set('limit', params.limit.toString());
      }
      if (params.offset !== undefined) {
        httpParams = httpParams.set('offset', params.offset.toString());
      }
    }

    return this.http.get<WorkRestrictionRulesResponse>(this.baseUrl, { params: httpParams });
  }

  /**
   * Получить районы сектора
   */
  getSectorDistricts(sectorId: number): Observable<SectorDistrictsResponse> {
    return this.http.get<SectorDistrictsResponse>(`${this.sectorDistrictsUrl}/${sectorId}/districts/`);
  }

  /**
   * Получить одно правило по ID
   */
  getRule(id: number): Observable<WorkRestrictionRule> {
    return this.http.get<WorkRestrictionRule>(this.baseUrl + '/' + id);
  }

  /**
   * Создать новое правило
   */
  createRule(payload: CreateWorkRestrictionRulePayload): Observable<WorkRestrictionRule> {
    return this.http.post<WorkRestrictionRule>(this.baseUrl + '/', payload);
  }

  /**
   * Обновить правило
   */
  updateRule(id: number, payload: Partial<CreateWorkRestrictionRulePayload>): Observable<WorkRestrictionRule> {
    return this.http.patch<WorkRestrictionRule>(this.baseUrl + '/' + id, payload);
  }

  /**
   * Удалить правило
   */
  deleteRule(id: number): Observable<void> {
    return this.http.delete<void>(this.baseUrl + '/' + id);
  }
}
